import type { ReactNode } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { cn } from '@poseidon/components/cn';
import { Button } from '@poseidon/components/Input2';
import { General } from '@poseidon/components/StrokeIcons';

const MIN_SCALE = 0.3;
const MAX_SCALE = 2.5;
const clampScale = (s: number) => Math.min(MAX_SCALE, Math.max(MIN_SCALE, s));

interface View {
  x: number;
  y: number;
  scale: number;
}

const INITIAL: View = { x: 48, y: 32, scale: 1 };

/**
 * A pannable, zoomable viewport. Children are rendered in a transformed layer (translate + scale,
 * origin top-left). Drag empty space to pan; scroll to zoom toward the cursor; zoom buttons in the
 * corner. Pointer-downs on buttons / inputs / `[data-no-pan]` elements don't start a pan, so the
 * nodes stay interactive. Mechanics mirror the Helm/touch graph viewports.
 */
export function SequencePanZoom({ children }: { children: ReactNode }) {
  const viewportRef = useRef<HTMLDivElement>(null);
  const [view, setView] = useState<View>(INITIAL);
  const [dragging, setDragging] = useState(false);
  const dragRef = useRef<{ sx: number; sy: number; ox: number; oy: number } | null>(null);
  // Zoom is a modifier gesture: hold Z (or Ctrl/Cmd) while scrolling. Plain scroll / two-finger pans.
  const zHeldRef = useRef(false);

  useEffect(() => {
    const isTyping = (t: EventTarget | null) =>
      t instanceof HTMLElement && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable);
    const down = (e: KeyboardEvent) => {
      // Bare Z is the zoom modifier; ignore Cmd/Ctrl+Z (undo) so the ref doesn't stick if the browser
      // swallows the keyup for the combo.
      if (e.key.toLowerCase() === 'z' && !e.metaKey && !e.ctrlKey && !isTyping(e.target)) zHeldRef.current = true;
    };
    const up = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() === 'z') zHeldRef.current = false;
    };
    const blur = () => {
      zHeldRef.current = false;
    };
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    window.addEventListener('blur', blur);
    return () => {
      window.removeEventListener('keydown', down);
      window.removeEventListener('keyup', up);
      window.removeEventListener('blur', blur);
    };
  }, []);

  // Wheel / two-finger: PAN by default; ZOOM (anchored to the cursor) only while Z or Ctrl/Cmd is held.
  useEffect(() => {
    const el = viewportRef.current;
    if (el == null) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      if (zHeldRef.current || e.ctrlKey || e.metaKey) {
        const rect = el.getBoundingClientRect();
        const cx = e.clientX - rect.left;
        const cy = e.clientY - rect.top;
        setView((v) => {
          const scale = clampScale(v.scale * (e.deltaY < 0 ? 1.1 : 1 / 1.1));
          const k = scale / v.scale;
          return { scale, x: cx - k * (cx - v.x), y: cy - k * (cy - v.y) };
        });
      } else {
        setView((v) => ({ ...v, x: v.x - e.deltaX, y: v.y - e.deltaY }));
      }
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  // Drag-to-pan via window listeners (robust across the node cards).
  useEffect(() => {
    if (!dragging) return;
    const move = (e: PointerEvent) => {
      const d = dragRef.current;
      if (d == null) return;
      setView((v) => ({ ...v, x: d.ox + (e.clientX - d.sx), y: d.oy + (e.clientY - d.sy) }));
    };
    const up = () => {
      setDragging(false);
      dragRef.current = null;
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
  }, [dragging]);

  const onPointerDown = (e: React.PointerEvent) => {
    if (e.button !== 0) return;
    if (e.target instanceof Element && e.target.closest('button, input, textarea, a, select, [data-no-pan]')) return;
    dragRef.current = { sx: e.clientX, sy: e.clientY, ox: view.x, oy: view.y };
    setDragging(true);
  };

  const zoomBy = useCallback((factor: number) => {
    const rect = viewportRef.current?.getBoundingClientRect();
    const cx = rect ? rect.width / 2 : 0;
    const cy = rect ? rect.height / 2 : 0;
    setView((v) => {
      const scale = clampScale(v.scale * factor);
      const k = scale / v.scale;
      return { scale, x: cx - k * (cx - v.x), y: cy - k * (cy - v.y) };
    });
  }, []);

  return (
    <div
      ref={viewportRef}
      onPointerDown={onPointerDown}
      className={cn(
        'relative size-full touch-none overflow-hidden bg-slate-50 dark:bg-neutral-900',
        dragging ? 'cursor-grabbing' : 'cursor-grab',
      )}>
      {/* Dot grid, tied to the pan/zoom so it reads as an infinite canvas. */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage: 'radial-gradient(circle, rgba(120,130,150,0.35) 1px, transparent 1px)',
          backgroundSize: `${24 * view.scale}px ${24 * view.scale}px`,
          backgroundPosition: `${view.x}px ${view.y}px`,
        }}
      />
      <div
        className="absolute left-0 top-0 origin-top-left"
        style={{ transform: `translate(${view.x}px, ${view.y}px) scale(${view.scale})` }}>
        {children}
      </div>

      <div
        className="absolute bottom-3 right-3 z-10 flex items-center gap-0.5 rounded-lg border border-gray-200 bg-white p-0.5 shadow-sm dark:border-gray-700 dark:bg-slate-900"
        onPointerDown={(e) => e.stopPropagation()}>
        <Button size="xs" text icon={<General.Minus />} tooltip="Zoom out" onClick={() => zoomBy(1 / 1.2)} />
        <Button size="xs" text tooltip="Reset view · hold Z (or Ctrl/⌘) + scroll to zoom" onClick={() => setView(INITIAL)}>
          {Math.round(view.scale * 100)}%
        </Button>
        <Button size="xs" text icon={<General.Plus />} tooltip="Zoom in" onClick={() => zoomBy(1.2)} />
      </div>
    </div>
  );
}
